import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, FormControlLabel, Stack, Switch } from "@mui/material";

import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import  { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Agg } from "system/query/Aggs";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SingleBarPreview from "./SingleBarPreview";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsColorPicker from "components/settingsConfig/uiComponents/SettingsColorPicker";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import QueryWidgetSettingsMissingQueryInfoAlert from "components/widgetParts/queryWidgetComposition/QueryWidgetSettingsMissingQueryInfoAlert";

export type SingleBarSettingsStateType = {
  customWidgetTitle: string;
  fillColor?: string;
  showDataValue: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
}

export const defaultSettings: SingleBarSettingsStateType = {
  customWidgetTitle: '',
  fillColor: undefined,
  showDataValue: true,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left'
}

export const defaultSingleBarChartRequest = {
  name: 'single-bar-chart',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE,
};

export default function SingleBarChartSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const requestBody = getSingleOriginalRequestByName(props.activeWidgetRequests, 'single-bar-chart')?.requestInit?.body || {};
  const aggs = requestBody?.aggs ?? {};
  const aggKeys = Object.keys(aggs) ?? {};
  const firstAggKey = aggKeys?.[0] ?? "";

  const [aggKey, setAggKey] = useState<string>(firstAggKey);
  const [keywordAgg, setKeywordAgg] = useState<Agg | null>(aggs?.[firstAggKey] ?? null);
  const [onlyIncludeRowsWithValue, setOnlyIncludeRowsWithValue] = useState<boolean>(!keywordAgg?.include_null ?? true);

  const updateAggs = () => {
    const label: string = aggKey || keywordAgg?.field || "_total";

    const widgetRequest: WidgetRequest = { ...defaultSingleBarChartRequest };
    let requestInitBody = { aggs: {} };

    if (keywordAgg !== undefined) {
      const newKeywordAgg = {
        ...keywordAgg,
        include_null: !onlyIncludeRowsWithValue
      };
      newKeywordAgg.aggs = {
        "%": {
          "op": "percent",
          "of_total": true
        }
      }
      requestInitBody.aggs = {
        [label]: {
          ...newKeywordAgg,
          include_null: !onlyIncludeRowsWithValue,
          aggs: {
            "%": {
              "op": "percent",
              "of_total": true
            }
          }
        }
      };
    } else {
      requestInitBody = {};
    }
    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({ settings: settingsState }, [widgetRequest]);
  }

  const updateWidgetData = () => {
    if (props.widget?.id && props.onUpdateWidgetData) {
      props.onUpdateWidgetData(props.widget?.id, {
        settings: settingsState
      })
    }
  }

  const updatedDefaultSettings = {...defaultSettings, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<SingleBarSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: SingleBarSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        updateWidgetData();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.single-bar-chart.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && <>
          {props.queryInfo && <>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={["keyword", "keywords"]}
              includeRecordsField={true}
              queryInfo={props.queryInfo}
              aggs={keywordAgg}
              setAggs={(newAggs: Agg | null) => setKeywordAgg(newAggs)}
              aggsLabel={aggKey}
              setAggsLabel={setAggKey}
              ></LeafAggSelector>
              <br/>
              {keywordAgg?.field &&
                <FormControlLabel
                  control={
                    <Switch
                      checked={onlyIncludeRowsWithValue}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setOnlyIncludeRowsWithValue(event.target.checked);
                      }}/>
                  }
                  label={i18n.t("requestOptions.only-rows-with-value")}
                  />
              }
          </>}
          {(!props.queryInfo) &&
            <QueryWidgetSettingsMissingQueryInfoAlert />
          }
        </>}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Graph Preview */}
            <SingleBarPreview settingsState={settingsState} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
                <SettingsCheckbox
                  checked={settingsState.showDataValue}
                  keyid="showDataValue"
                  label={i18n.t("widget-types.single-bar-chart.settings.show-data-value")} />
              </SettingsAccordion>
              <SettingsColorPicker
                color={settingsState.fillColor ?? undefined}
                defaultColor={undefined}
                keyid="fillColor"
                label={i18n.t("fill-color")} />
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}
